/**
 * Step 4 (Supporting evidence) — supporting documents and additional forms
 * upload, as a file upload inside the List & Loop (Multiple Responses)
 * pattern.
 */
import React from 'react';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  fileInputUI,
  fileInputSchema,
  selectUI,
  selectSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { MISSING_FILE } from 'platform/forms-system/src/js/validation';
import get from 'platform/utilities/data/get';
import {
  FILE_TYPES,
  HINT_TEXT,
  UPLOAD_URL,
  ATTACHMENTS_TYPE,
  ADDITIONAL_ATTACHMENT_LABEL,
} from '../../../shared/components/fileInputComponent/constants';
import {
  createPayload,
  parseResponse,
} from '../../../shared/utils/fileInputMultiUIConfig';
import { additionalSupportAccordionItem } from '../../../shared/content/supportingEvidenceOrientation';
import { prepareFilesAccordionItem } from '../../content/fileUpload';
import {
  hasAdditionalEvidence,
  listLoopPatternWithBypassedIntro,
  navForwardFromSummary,
} from './utils';

/** Loop summary page path. */
export const ADDITIONAL_EVIDENCE_SUMMARY_PATH =
  'supporting-evidence/additional-evidence-summary';

/** Item page path (with :index) — used to deep-link into the loop. */
export const ADDITIONAL_EVIDENCE_ITEM_PATH =
  'supporting-evidence/additional-evidence-upload/:index/document';

/**
 * Loop descriptor for the skip-intro navigation helpers: entering the loop
 * goes straight to the first item page (add mode) or the loop summary.
 */
export const ADDITIONAL_EVIDENCE_LOOP = {
  arrayPath: 'additionalDocumentsLoop',
  itemPath: ADDITIONAL_EVIDENCE_ITEM_PATH,
  summaryPath: ADDITIONAL_EVIDENCE_SUMMARY_PATH,
  // The summary page's arrayBuilderYesNoUI field; the summary's
  // `onNavForward` branches on it the same way platform's default does.
  hasItemsKey: 'view:hasAnotherSupportingDocument',
};

/**
 * Decision page that gates the additional evidence loop. The additional
 * evidence URLs are unchanged in the Step 4 URL spec in Figma (#157626).
 */
export const ADDITIONAL_EVIDENCE_INTRO_PATH =
  'supporting-evidence/additional-evidence-intro';

/**
 * Intro-bypass pattern: bypass the loop's intro page. All routes
 * that would land there go to the additional-evidence decision page
 * instead.
 */
export const additionalEvidenceBypassedIntro = listLoopPatternWithBypassedIntro(
  ADDITIONAL_EVIDENCE_LOOP,
  ADDITIONAL_EVIDENCE_INTRO_PATH,
);

/** Value-to-label map for the document-type select and summary cards. */
export const ATTACHMENT_LABELS = ATTACHMENTS_TYPE.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

/** @type {ArrayBuilderOptions} */
export const arrayBuilderOptions = {
  arrayPath: 'additionalDocumentsLoop',
  nounSingular: 'supporting document',
  nounPlural: 'supporting documents',
  // Optional multi-page pattern (VADS "Multiple responses"): the additional
  // evidence intro page already captures the Yes/No, so the loop itself does
  // not force at least one item.
  required: false,
  isItemIncomplete: item =>
    !item?.uploadedDocument?.confirmationCode || !item?.attachmentId,
  text: {
    getItemName: item => item?.uploadedDocument?.name || 'Supporting document',
    cardDescription: item => ATTACHMENT_LABELS[item?.attachmentId] || '',
    cancelAddButtonText: () => 'Cancel adding this document',
    cancelAddYes: 'Yes, cancel adding this document',
    cancelAddNo: 'No, continue adding this document',
    deleteTitle: 'Delete file?',
    deleteDescription: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return `We’ll delete this file: ${itemName}`;
    },
    deleteYes: () => 'Delete file',
    deleteNo: () => 'Keep file',
    alertItemDeleted: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return `We’ve deleted file: ${itemName}`;
    },
  },
};

/**
 * Shown in place of the summary cards (e.g. after deleting the last
 * remaining document). Placed as its own schema field — rather than in the
 * summary page's title/description text overrides — so it renders after the
 * page's alerts/cards block instead of above it.
 */
const NoSupportingDocumentsMessage = () => (
  <p>
    There are no supporting documents or additional forms associated with this
    claim.
  </p>
);

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:noSupportingDocumentsMessage': {
      'ui:description': NoSupportingDocumentsMessage,
      'ui:options': {
        hideIf: formData =>
          !!get(ADDITIONAL_EVIDENCE_LOOP.arrayPath, formData)?.length,
      },
    },
    'view:hasAnotherSupportingDocument': arrayBuilderYesNoUI(
      arrayBuilderOptions,
      {
        title: 'Do you have a supporting document to upload?',
        labels: {
          Y: 'Yes',
          N: 'No',
        },
      },
      {
        title: 'Do you have another supporting document to upload?',
        labels: {
          Y: 'Yes',
          N: 'No',
        },
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:noSupportingDocumentsMessage': { type: 'object', properties: {} },
      'view:hasAnotherSupportingDocument': arrayBuilderYesNoSchema,
    },
    required: ['view:hasAnotherSupportingDocument'],
  },
};

/**
 * Item page: single file upload plus a document-type select rendered as a
 * separate form control (decoupled from the file card, per the VADS
 * recommendation).
 * @returns {PageSchema}
 */
const uploadPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'Upload supporting documents and additional forms',
      nounSingular: arrayBuilderOptions.nounSingular,
      hasMultipleItemPages: false,
    }),
    uploadedDocument: {
      ...fileInputUI({
        title: 'Upload supporting documents or additional forms',
        hint: HINT_TEXT,
        required: () => true,
        accept: FILE_TYPES,
        fileUploadUrl: UPLOAD_URL,
        formNumber: '21-526EZ',
        fileSizesByFileType: {
          pdf: {
            maxFileSize: 1024 * 1024 * 100,
            minFileSize: 1024,
          },
          default: {
            maxFileSize: 1024 * 1024 * 50,
            minFileSize: 1,
          },
        },
        errorMessages: { required: MISSING_FILE },
        createPayload,
        parseResponse,
      }),
      'ui:validations': [
        // Needed for validation to work correctly inside arrays; taken from
        // the ownedAssetPages.js precedent.
        // https://va.ghe.com/software/vets-design-system-documentation/issues/4837
        (errors, fieldData) => {
          if (fieldData?.isEncrypted && !fieldData?.confirmationCode) {
            return;
          }
          if (!fieldData || !fieldData.name) {
            errors.addError(MISSING_FILE);
          }
          if (fieldData?.errorMessage) {
            errors.addError(fieldData.errorMessage);
          }
        },
      ],
    },
    attachmentId: selectUI({
      title: ADDITIONAL_ATTACHMENT_LABEL,
      required: () => true,
      labels: ATTACHMENT_LABELS,
      errorMessages: { required: 'Choose a document type' },
      // explicit aria-describedby text, read together with the select's label.
      messageAriaDescribedby: 'Choose the type of document you uploaded above.',
    }),
  },
  schema: {
    type: 'object',
    required: ['uploadedDocument', 'attachmentId'],
    properties: {
      uploadedDocument: fileInputSchema(),
      attachmentId: selectSchema(Object.keys(ATTACHMENT_LABELS)),
    },
  },
};

/**
 * Array-builder page set for the additional documents List & Loop flow.
 * Spread into uploadAndSummaryPages in place of the previous
 * additionalEvidenceUpload and additionalEvidenceSummary placeholder pages.
 */
export const additionalEvidenceUploadPages = arrayBuilderPages(
  arrayBuilderOptions,
  pageBuilder => ({
    additionalEvidenceSummary: pageBuilder.summaryPage({
      title: 'Review your supporting documents and additional forms',
      path: ADDITIONAL_EVIDENCE_SUMMARY_PATH,
      depends: hasAdditionalEvidence,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
      // Replaces platform's default navForward so the next page is worked
      // out with getPathAfterLoop, which correctly skips this loop's own
      // expanded item pages.
      onNavForward: navForwardFromSummary(ADDITIONAL_EVIDENCE_LOOP),
      // Intro-bypass pattern: Back goes to the decision page.
      ...additionalEvidenceBypassedIntro.summaryPage,
    }),
    additionalEvidenceUpload: pageBuilder.itemPage({
      title: 'Upload supporting documents and additional forms',
      path: ADDITIONAL_EVIDENCE_ITEM_PATH,
      depends: hasAdditionalEvidence,
      uiSchema: uploadPage.uiSchema,
      schema: uploadPage.schema,
      // Intro-bypass pattern: Back returns to the loop summary if items
      // remain, otherwise to the decision page.
      ...additionalEvidenceBypassedIntro.itemPage,
      // rendered between the cancel button and the nav buttons.
      ContentBeforeButtons: () => (
        <va-accordion>
          {additionalSupportAccordionItem}
          {prepareFilesAccordionItem}
        </va-accordion>
      ),
    }),
  }),
);

export default additionalEvidenceUploadPages;
